// @ts-nocheck
import { FC, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { IconButton, Paper, Table, TableBody, TableContainer, TableFooter, TableHead, TableRow } from "@mui/material";
import { FirstPage, KeyboardArrowLeft, KeyboardArrowRight, LastPage } from "@mui/icons-material";
import { v4 } from "uuid";

import { useUser } from "@framework/provider-user";
import { IUser } from "@framework/types";

import { useScreenSize } from "../../../../../../../../utils/screen";
import { useFormatDateHumanReadable } from "../../../../../../../../hooks";
import { ITableData } from "./interfaces";
import { StyledHeaderTableCell, StyledTableCell } from "./styled";
import { formatMeasurementNumber } from "../../../../utils/measurement-format";

export interface IAnalyticsTable {
  data: ITableData;
}

export const AnalyticsTable: FC<IAnalyticsTable> = props => {
  const {
    data: { rows, items, type },
  } = props;

  const { isExtraLargeScreen, isLargeScreen } = useScreenSize();
  const formatDate = useFormatDateHumanReadable();
  const { profile } = useUser<IUser>();

  const headersId = "pages.scheme.analytics.headers";

  const [page, setPage] = useState(0);

  const validItems = useMemo(() => items, [items]);

  const itemsPerPage = isExtraLargeScreen ? 10 : isLargeScreen ? 5 : 3;
  const maxPage = Math.floor(validItems.length / itemsPerPage);
  const lastPage = validItems.length % itemsPerPage === 0 ? maxPage - 1 : maxPage;

  const visibleItemsCount = Math.min(validItems.length - page * itemsPerPage, itemsPerPage);
  const totalColumns = visibleItemsCount + 1;
  const columnWidth = `${100 / (totalColumns || 1)}%`;

  const tableRows = useMemo(() => {
    return rows.map((date, dateIndex) => {
      return {
        id: date,
        date: formatDate(date),
        values: validItems.map(item => item.values[dateIndex]),
        values2: validItems.map(item => item.values2?.[dateIndex] ?? null),
      };
    });
  }, [rows, validItems, formatDate]);

  const handleChangePage = (newPage: number) => () => {
    setPage(newPage);
  };

  useEffect(() => {
    setPage(0);
  }, [items, type, rows]);

  return (
    <TableContainer
      elevation={0}
      component={Paper}
      sx={{ mt: 0, maxHeight: "100%", width: "100%", borderRadius: "8px", border: "1px solid #EBEBF7" }}
    >
      <Table
        sx={{
          minWidth: 300,
          width: "100%",
          borderCollapse: "collapse",
        }}
        aria-label="analytics table"
        stickyHeader
      >
        <TableHead>
          <TableRow>
            <StyledHeaderTableCell
              sx={{
                textAlign: "center",
                width: columnWidth,
                minWidth: columnWidth,
                maxWidth: columnWidth,
                top: 0,
                zIndex: 3,
                backgroundColor: "#fafaff",
                boxShadow: "inset -1px 0 0 0 #ebebf7, inset 0 -1px 0 0 #ebebf7",
              }}
              rowSpan={2}
            >
              <FormattedMessage id={`${headersId}.date`} />
            </StyledHeaderTableCell>
            <StyledHeaderTableCell
              sx={{
                textAlign: "center",
                top: 0,
                zIndex: 2,
                backgroundColor: "#fafaff",
                boxShadow: "inset 0 -1px 0 0 #ebebf7",
              }}
              colSpan={visibleItemsCount}
            >
              <FormattedMessage id={`${headersId}.${type}`} />
            </StyledHeaderTableCell>
          </TableRow>
          <TableRow>
            {validItems.slice(page * itemsPerPage, page * itemsPerPage + itemsPerPage).map(item => (
              <StyledHeaderTableCell
                key={v4()}
                sx={{
                  textAlign: "center",
                  width: columnWidth,
                  minWidth: columnWidth,
                  maxWidth: columnWidth,
                  top: { xs: "19px", xxl: "23px" },
                  position: "sticky",
                  zIndex: 2,
                  backgroundColor: "#fafaff",
                  boxShadow: "inset 0 -1px 0 0 #ebebf7",
                }}
              >
                {item.name}
              </StyledHeaderTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {tableRows.map(row =>
            row.values.some(value => value !== null) ? (
              <TableRow key={v4()}>
                <StyledTableCell
                  sx={{
                    textAlign: "center",
                    width: columnWidth,
                    minWidth: columnWidth,
                    maxWidth: columnWidth,
                  }}
                >
                  {row.date}
                </StyledTableCell>
                {row.values.slice(page * itemsPerPage, page * itemsPerPage + itemsPerPage).map((value, idx) => {
                  const item = validItems[page * itemsPerPage + idx];
                  const value2 = row.values2[page * itemsPerPage + idx];
                  const formatted1 = formatMeasurementNumber(
                    value,
                    item?.unitCode ?? null,
                    profile.interfaceSettings.dataFormat,
                    { fallbackDigits: 2 },
                  );
                  const formatted2 =
                    value2 !== null && value2 !== undefined
                      ? formatMeasurementNumber(value2, item?.unitCode2 ?? null, profile.interfaceSettings.dataFormat, {
                          fallbackDigits: 2,
                        })
                      : null;
                  return (
                    <StyledTableCell
                      key={v4()}
                      sx={{
                        textAlign: "center",
                        width: columnWidth,
                        minWidth: columnWidth,
                        maxWidth: columnWidth,
                      }}
                    >
                      {formatted2 !== null ? `${formatted1} / ${formatted2}` : formatted1}
                    </StyledTableCell>
                  );
                })}
              </TableRow>
            ) : null,
          )}
        </TableBody>
        {validItems.length > itemsPerPage ? (
          <TableFooter>
            <TableRow>
              <StyledTableCell colSpan={visibleItemsCount + 1} sx={{ textAlign: "right" }}>
                <IconButton size="small" onClick={handleChangePage(0)} disabled={page === 0}>
                  <FirstPage />
                </IconButton>
                <IconButton size="small" onClick={handleChangePage(page - 1)} disabled={page === 0}>
                  <KeyboardArrowLeft />
                </IconButton>
                <IconButton size="small" onClick={handleChangePage(page + 1)} disabled={page === lastPage}>
                  <KeyboardArrowRight />
                </IconButton>
                <IconButton size="small" onClick={handleChangePage(lastPage)} disabled={page === lastPage}>
                  <LastPage />
                </IconButton>
              </StyledTableCell>
            </TableRow>
          </TableFooter>
        ) : null}
      </Table>
    </TableContainer>
  );
};
